import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Upload, Sparkles, ExternalLink, Download, FileStack } from "lucide-react";
import api, { errMsg, inr } from "../lib/api";
import PageHeader from "../components/PageHeader";
import StatusBadge from "../components/StatusBadge";

const emptyOA = { oaNumber: "", oaDate: "", customerName: "", partyPONumber: "", destination: "", deliveryAddress: "", gstNumber: "", netTotal: 0 };

export default function ImportOldOA() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyOA);
  const [origFile, setOrigFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [onDuplicate, setOnDuplicate] = useState("skip");
  const fileRef = useRef();
  const pdfRef = useRef();

  const load = async () => { const { data } = await api.get("/old-oa"); setRows(data); };
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const extract = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const up = await api.post("/import/old-oa/upload-file", (() => { const x = new FormData(); x.append("file", f); return x; })(), { headers: { "Content-Type": "multipart/form-data" } });
      setOrigFile(up.data);
      toast.info("Extracting with AI…");
      const { data } = await api.post("/import/old-oa/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const e = data.extracted || {};
      setForm({ oaNumber: e.oaNumber || "", oaDate: e.oaDate || "", customerName: e.customerName || "", partyPONumber: e.partyPONumber || "", destination: e.destination || "", deliveryAddress: e.deliveryAddress || "", gstNumber: e.gstNumber || "", netTotal: e.netTotal || 0, products: e.products || [] });
      toast.success("AI extraction complete — review and import.");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const uploadOriginalOnly = async (f) => {
    if (!f) return;
    try { const x = new FormData(); x.append("file", f); const up = await api.post("/import/old-oa/upload-file", x, { headers: { "Content-Type": "multipart/form-data" } }); setOrigFile(up.data); toast.success("Original file attached."); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const importOne = async () => {
    if (!form.oaNumber || !form.customerName) return toast.error("OA Number and Customer are required.");
    setBusy(true);
    try {
      const rowData = { ...form, importedFile: origFile };
      const { data } = await api.post("/import/old_oa/commit", { rows: [{ data: rowData, errors: [] }], onDuplicate });
      if (data.imported) toast.success(`Imported ${form.oaNumber}`);
      else if (data.skipped) toast.warning("OA Number already exists — skipped.");
      else if (data.replaced) toast.success(`Replaced ${form.oaNumber}`);
      setForm(emptyOA); setOrigFile(null); if (pdfRef.current) pdfRef.current.value = ""; if (fileRef.current) fileRef.current.value = ""; load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const openFile = (path) => {
    api.get("/old-oa/file", { params: { path, mode: "view" }, responseType: "blob" })
      .then(({ data }) => { const url = URL.createObjectURL(data); window.open(url, "_blank"); setTimeout(() => URL.revokeObjectURL(url), 60000); })
      .catch((e) => toast.error(errMsg(e)));
  };

  return (
    <div>
      <PageHeader title="Import Old OA" subtitle="Import historical OA records — original numbers preserved" />
      <div className="p-6 lg:p-8 space-y-6">
        <div className="card p-6">
          <h3 className="font-head font-bold text-lg mb-4 flex items-center gap-2"><Sparkles className="h-5 w-5 text-amber-600" /> Import a Historical OA</h3>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center cursor-pointer hover:border-amber-400" onClick={() => pdfRef.current?.click()} data-testid="oldoa-ai-dropzone">
              <Sparkles className="h-6 w-6 text-amber-500 mx-auto mb-1" />
              <div className="text-sm font-semibold">AI Extract from PDF</div>
              <div className="text-xs text-slate-400">Upload a PDF — we pre-fill the form for you</div>
              <input ref={pdfRef} type="file" accept=".pdf" className="hidden" onChange={(e) => extract(e.target.files[0])} data-testid="oldoa-pdf-input" />
            </div>
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center cursor-pointer hover:border-amber-400" onClick={() => fileRef.current?.click()} data-testid="oldoa-orig-dropzone">
              <Upload className="h-6 w-6 text-slate-400 mx-auto mb-1" />
              <div className="text-sm font-semibold">Attach Original File</div>
              <div className="text-xs text-slate-400">{origFile ? origFile.filename : "Excel/PDF/Image — stored as-is"}</div>
              <input ref={fileRef} type="file" className="hidden" onChange={(e) => uploadOriginalOnly(e.target.files[0])} data-testid="oldoa-orig-input" />
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            <div><label className="label">Original OA Number *</label><input className="input" value={form.oaNumber} onChange={(e) => set("oaNumber", e.target.value)} data-testid="oldoa-number" /></div>
            <div><label className="label">OA Date</label><input type="date" className="input" value={form.oaDate} onChange={(e) => set("oaDate", e.target.value)} data-testid="oldoa-date" /></div>
            <div><label className="label">Customer *</label><input className="input" value={form.customerName} onChange={(e) => set("customerName", e.target.value)} data-testid="oldoa-customer" /></div>
            <div><label className="label">PO Number</label><input className="input" value={form.partyPONumber} onChange={(e) => set("partyPONumber", e.target.value)} /></div>
            <div><label className="label">Destination</label><input className="input" value={form.destination} onChange={(e) => set("destination", e.target.value)} /></div>
            <div><label className="label">GST Number</label><input className="input" value={form.gstNumber} onChange={(e) => set("gstNumber", e.target.value)} /></div>
            <div className="md:col-span-2"><label className="label">Delivery Address</label><input className="input" value={form.deliveryAddress} onChange={(e) => set("deliveryAddress", e.target.value)} /></div>
            <div><label className="label">Net Total</label><input type="number" className="input" value={form.netTotal} onChange={(e) => set("netTotal", parseFloat(e.target.value) || 0)} data-testid="oldoa-net" /></div>
          </div>
          <div className="flex items-center justify-end gap-3 mt-4">
            <select className="input w-44" value={onDuplicate} onChange={(e) => setOnDuplicate(e.target.value)} data-testid="oldoa-on-duplicate">
              <option value="skip">Skip if exists</option><option value="replace">Replace existing</option><option value="separate">Import as separate</option>
            </select>
            <button className="btn-primary" onClick={importOne} disabled={busy} data-testid="oldoa-import-btn">{busy ? "Working…" : "Import OA"}</button>
          </div>
        </div>

        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 font-head font-semibold flex items-center gap-2"><FileStack className="h-4 w-4 text-amber-600" /> Imported / Old OA ({rows.length})</div>
          <div className="overflow-x-auto"><table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200"><tr>
              <th className="th">OA No.</th><th className="th">Customer</th><th className="th">OA Date</th><th className="th">Net Total</th><th className="th">Imported By</th><th className="th">Imported Date</th><th className="th">Source</th><th className="th">Status</th><th className="th">File</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {rows.length === 0 && <tr><td className="td text-slate-400" colSpan={9}>No imported OA yet.</td></tr>}
              {rows.map((o) => (
                <tr key={o.id} className="hover:bg-slate-50" data-testid={`oldoa-row-${o.oaNumber}`}>
                  <td className="td font-mono font-semibold">{o.oaNumber}</td>
                  <td className="td">{o.customerName}</td>
                  <td className="td">{o.oaDate || "—"}</td>
                  <td className="td font-semibold">{inr(o.netTotal)}</td>
                  <td className="td">{o.importedBy || o.createdByName}</td>
                  <td className="td text-xs text-slate-400">{(o.importedDate || "").slice(0, 10)}</td>
                  <td className="td"><span className="badge bg-purple-50 text-purple-700 border border-purple-200">IMPORTED</span></td>
                  <td className="td"><StatusBadge status={o.workStatus || "completed"} /></td>
                  <td className="td">{o.importedFile?.storagePath ? <button className="p-1.5 rounded hover:bg-slate-100" onClick={() => openFile(o.importedFile.storagePath)} data-testid={`oldoa-file-${o.oaNumber}`}><ExternalLink className="h-4 w-4 text-slate-600" /></button> : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      </div>
    </div>
  );
}
